import React, { useMemo, useState } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

import dayjs from 'dayjs';

import { UpliftIndexRate } from '@/graphql/contracts/_generated_/contracts';
import { NumberUtils } from '@/utils/numbers.utils';
import DatePickerTag4 from '../DatePicker/DatePickerTag4';
import styles from './line-chart.module.scss';

type FrequencyType = 'month' | 'quarter' | 'year';

interface LineChartProps {
  data: UpliftIndexRate[];
  width?: number;
  height?: number;
}

const chartMargins = { top: 20, right: 30, left: 20, bottom: 60 };

const chartColors = {
  gradientStart: '#1e40af',
  gradientMiddle: '#3b82f6', 
  gradientEnd: '#60a5fa',
  areaGradientStart: '#3b82f6',
  gridStroke: '#e2e8f0',
  axisStroke: '#334155',
  axisTickColor: '#64748b',
  lineDotFill: '#1e40af',
  lineDotStroke: 'white'
};

const chartStyles = {
  gridStrokeDasharray: '3 3',
  gridStrokeOpacity: 0.6,
  axisStrokeWidth: 1,
  axisTickFontSize: 11,
  yAxisTickFontSize: 12,
  axisTickFontWeight: 500,
  lineStrokeWidth: 3,
  lineDotStrokeWidth: 2,
  lineDotRadius: 6,
  lineActiveDotRadius: 8,
  lineActiveDotStrokeWidth: 3,
  yDomainPadding: 0.05
};

const xAxisTickStyle = {
  fontSize: chartStyles.axisTickFontSize,
  fill: chartColors.axisTickColor,
  fontWeight: chartStyles.axisTickFontWeight
};

const yAxisTickStyle = {
  fontSize: chartStyles.yAxisTickFontSize,
  fill: chartColors.axisTickColor,
  fontWeight: chartStyles.axisTickFontWeight
};

const axisLineStyle = {
  stroke: chartColors.axisStroke,
  strokeWidth: chartStyles.axisStrokeWidth
};

const yAxisDomain = [
  `dataMin - ${chartStyles.yDomainPadding}`,
  `dataMax + ${chartStyles.yDomainPadding}`
];

const lineDotStyle = {
  fill: chartColors.lineDotFill,
  stroke: chartColors.lineDotStroke,
  strokeWidth: chartStyles.lineDotStrokeWidth,
  r: chartStyles.lineDotRadius,
  cursor: 'pointer'
};

const lineActiveDotStyle = {
  r: chartStyles.lineActiveDotRadius,
  fill: chartColors.lineDotFill,
  stroke: chartColors.lineDotStroke,
  strokeWidth: chartStyles.lineActiveDotStrokeWidth,
  cursor: 'pointer'
};

export const LineChart: React.FC<LineChartProps> = ({ 
  data, 
  height = 450,
}) => {
  const [selectedPoint, setSelectedPoint] = useState<number | null>(null);
  const [frequency, setFrequency] = useState<FrequencyType>('month');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const handleStartDateChange = ({ value }: { value: Date | null }) => {
    if (value) {
      setStartDate(dayjs(value).format('YYYY-MM'));
    } else {
      setStartDate('');
    }
  };

  const handleEndDateChange = ({ value }: { value: Date | null }) => {
    if (value) {
      setEndDate(dayjs(value).format('YYYY-MM'));
    } else {
      setEndDate('');
    }
  };

  const aggregateDataByFrequency = (rawData: UpliftIndexRate[], freq: FrequencyType) => {
    if (!rawData || rawData.length === 0) return [];

    const sortedData = [...rawData].sort((a, b) => 
      new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime()
    );
    const grouped = new Map<string, UpliftIndexRate[]>();

    sortedData.forEach(item => {
      const date = new Date(item.effectiveDate);
      let key: string;

      switch (freq) {
        case 'quarter':
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          key = `${date.getFullYear()}-Q${quarter}`;
          break;
        case 'year':
          key = `${date.getFullYear()}`;
          break;
        default: // month
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          break;
      }

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    return Array.from(grouped.entries()).map(([key, items], index) => {
      const rates = items.map(item => parseFloat(item.rate));
      const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
      const latestItem = items[items.length - 1];

      let formattedDate: string;
      let fullDate: string;

      if (freq === 'quarter') {
        const [year, quarter] = key.split('-');
        formattedDate = `${quarter} ${year.slice(-2)}`;
        fullDate = `${quarter} ${year}`;
      } else if (freq === 'year') {
        formattedDate = key.slice(-2);
        fullDate = key;
      } else {
        const date = new Date(latestItem.effectiveDate);
        formattedDate = date.toLocaleDateString('en-GB', {
          month: 'short',
          year: '2-digit'
        });
        fullDate = date.toLocaleDateString('en-GB', {
          month: 'short',
          year: 'numeric'
        });
      }

      return {
        effectiveDate: latestItem.effectiveDate,
        rate: avgRate,
        index,
        formattedDate,
        fullDate,
        originalItems: items
      };
    });
  };

  const { fullData, chartData } = useMemo(() => {
    const fullProcessedData = aggregateDataByFrequency(data, frequency);
    let filteredData = fullProcessedData;

    if (startDate || endDate) {
      filteredData = fullProcessedData.filter(item => {
        const itemDate = new Date(item.effectiveDate);
        
        if (startDate) {
          const [startYear, startMonth] = startDate.split('-').map(Number);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1; // getMonth() is 0-based
          
          if (itemYear < startYear || (itemYear === startYear && itemMonth < startMonth)) {
            return false;
          }
        }
        
        if (endDate) {
          const [endYear, endMonth] = endDate.split('-').map(Number);
          const itemYear = itemDate.getFullYear();
          const itemMonth = itemDate.getMonth() + 1; // getMonth() is 0-based
          
          if (itemYear > endYear || (itemYear === endYear && itemMonth > endMonth)) {
            return false;
          }
        }
        
        return true;
      });
    }

    return { fullData: fullProcessedData, chartData: filteredData };
  }, [data, frequency, startDate, endDate]);

  const rawMonthlyData = useMemo(() => {
    return [...data].sort((a, b) => 
      new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime()
    ).map(item => ({
      ...item,
      rate: parseFloat(item.rate)
    }));
  }, [data]);

  const currentRateChange = useMemo(() => {
    if (data.length === 0) {
      return null;
    }

    const currentRate = rawMonthlyData[rawMonthlyData.length - 1]?.rate;
    const prevRate = rawMonthlyData.length > 1 ? rawMonthlyData[rawMonthlyData.length - 2]?.rate : null;
    const currentChange = prevRate ? ((currentRate - prevRate) / prevRate) * 100 : null;
    
    return currentChange;
  }, [data, rawMonthlyData]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const pointData = payload[0].payload;
      const currentRate = payload[0].value;
      
      const currentIndex = fullData.findIndex(item => item.effectiveDate === pointData.effectiveDate);
      const prevRate = currentIndex > 0 ? fullData[currentIndex - 1]?.rate : null;
      const change = prevRate ? currentRate - prevRate : null;
      const changePercent = prevRate ? ((change! / prevRate) * 100) : null;

      return (
        <div className={styles.customTooltip}>
          <div className={styles.tooltipHeader}>
            <strong>{pointData.fullDate}</strong>
          </div>
          <div className={styles.tooltipContent}>
            <div className={styles.rateValue}>
              {frequency === 'month' ? 'Rate' : 'Avg Rate'}: 
              <strong>{NumberUtils.prettifyWithRound(currentRate, 3)}</strong>
            </div>
            {change !== null && (
              <div className={`${styles.changeValue} ${change >= 0 ? styles.positive : styles.negative}`}>
                Change: <strong>{change >= 0 ? '+' : ''}{NumberUtils.prettifyWithRound(change, 3)}</strong>
                {changePercent !== null && (
                  <span className={styles.percentChange}>
                    ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(2)}%)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const handlePointClick = (event: any) => {
    if (event && event.activePayload && event.activePayload[0]) {
      const index = event.activePayload[0].payload.index;
      setSelectedPoint(selectedPoint === index ? null : index);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No rate data available for the selected index.</p>
      </div>
    );
  }

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartControls}>
        <div className={styles.frequencyControls}>
          <span className={styles.controlLabel}>Frequency:</span>
          <div className={styles.radioGroup}>
            {(['month', 'quarter', 'year'] as FrequencyType[]).map((freq) => (
              <label key={freq} className={styles.radioLabel}>
                <input
                  type="radio"
                  value={freq}
                  checked={frequency === freq}
                  onChange={(e) => setFrequency(e.target.value as FrequencyType)}
                  className={styles.radioInput}
                />
                <span className={styles.radioText}>
                  {freq.charAt(0).toUpperCase() + freq.slice(1)}
                </span>
              </label>
            ))}
          </div>
        </div>
        
        <div className={styles.dateRangeControls}>
          <div className="col">
            From:
            <DatePickerTag4
              value={startDate ? dayjs(`${startDate}-01`).toDate() : null}
              onChange={handleStartDateChange}
              id="chart-start-date"
              placeholder="MM/YYYY"
             />
            </div>
           <div className="col">
            To:
            <DatePickerTag4
              value={endDate ? dayjs(`${endDate}-01`).toDate() : null}
              onChange={handleEndDateChange}
              id="chart-end-date"
              placeholder="MM/YYYY"
            />
          </div>
          {(startDate || endDate) && (
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              className={styles.clearButton}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className={styles.chartHeader}>
        <div className={styles.chartStats}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Latest Rate:</span>
            <span className={styles.statValue}>
              {NumberUtils.prettifyWithRound(rawMonthlyData[rawMonthlyData.length - 1]?.rate, 3)}
              {currentRateChange !== null && (
                <span className={`${styles.percentageChange} ${
                  currentRateChange >= 0 ? styles.positive : styles.negative
                }`}>
                  ({currentRateChange >= 0 ? '+' : ''}{currentRateChange.toFixed(2)}%)
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={height}>
          <RechartsLineChart
            data={chartData}
            margin={chartMargins}
            onClick={handlePointClick}
          >
            <defs>
              <linearGradient id="financialLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={chartColors.gradientStart} />
                <stop offset="50%" stopColor={chartColors.gradientMiddle} />
                <stop offset="100%" stopColor={chartColors.gradientEnd} />
              </linearGradient>
              <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={chartColors.areaGradientStart} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColors.areaGradientStart} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray={chartStyles.gridStrokeDasharray} 
              stroke={chartColors.gridStroke} 
              strokeOpacity={chartStyles.gridStrokeOpacity} 
            />
            
            <XAxis 
              dataKey="formattedDate"
              tick={xAxisTickStyle}
              axisLine={axisLineStyle}
              tickLine={{ stroke: chartColors.axisStroke }}
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
            />
            
            <YAxis 
              tick={yAxisTickStyle}
              axisLine={axisLineStyle}
              tickLine={{ stroke: chartColors.axisStroke }}
              domain={yAxisDomain}
              tickFormatter={(value) => NumberUtils.prettifyWithRound(parseFloat(value).toFixed(2))}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            <Line 
              type="monotone" 
              dataKey="rate" 
              stroke="url(#financialLineGradient)"
              strokeWidth={chartStyles.lineStrokeWidth}
              dot={lineDotStyle}
              activeDot={lineActiveDotStyle}
              connectNulls={false}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
